import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  avatar: string;
  memberSince: string;
  phone: string;
  address: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => { success: boolean; error?: string };
  register: (name: string, email: string, password: string) => { success: boolean; error?: string };
  logout: () => void;
  changePassword: (currentPw: string, newPw: string) => { success: boolean; error?: string };
  isLoading: boolean;
}

const MOCK_USERS = [
  {
    id: 'usr_001',
    email: 'admin@admin.com',
    password: 'admin123',
    name: 'Admin Kullanıcı',
    avatar: 'AK',
    memberSince: 'Ocak 2023',
    phone: '+90 532 100 00 00',
    address: 'Bağcılar Mah. Örnek Sk. No:12, İstanbul',
  },
];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('fb_user');
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
    setIsLoading(false);
  }, []);

  const login = (email: string, password: string): { success: boolean; error?: string } => {
    const found = MOCK_USERS.find(
      (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );
    if (!found) {
      return { success: false, error: 'E-posta veya şifre hatalı.' };
    }
    const { password: _pw, ...userData } = found;
    setUser(userData);
    localStorage.setItem('fb_user', JSON.stringify(userData));
    return { success: true };
  };

  const register = (name: string, email: string, password: string): { success: boolean; error?: string } => {
    const exists = MOCK_USERS.find((u) => u.email.toLowerCase() === email.toLowerCase());
    if (exists) {
      return { success: false, error: 'Bu e-posta adresi zaten kayıtlı.' };
    }
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    const newUser = {
      id: `usr_${Date.now()}`,
      email,
      password,
      name,
      avatar: initials,
      memberSince: new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' }),
      phone: '',
      address: '',
    };
    MOCK_USERS.push(newUser);
    const { password: _pw, ...userData } = newUser;
    setUser(userData);
    localStorage.setItem('fb_user', JSON.stringify(userData));
    return { success: true };
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('fb_user');
  };

  const changePassword = (currentPw: string, newPw: string): { success: boolean; error?: string } => {
    if (!user) return { success: false, error: 'Not logged in' };
    const found = MOCK_USERS.find(
      (u) => u.email.toLowerCase() === user.email.toLowerCase() && u.password === currentPw
    );
    if (!found) {
      return { success: false, error: 'errCurrentPassword' };
    }
    found.password = newPw;
    return { success: true };
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, changePassword, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}